import { Trophy, TrendingUp } from "lucide-react";
import ResponsibleBanner from "@/components/ResponsibleBanner";
import PremiumPicksBanner from "@/components/PremiumPicksBanner";
import { usePerformance, fmtUnits, sinceLabel, unitColor } from "@/components/TrackRecord";
import { RECENT_RESULTS, PERFORMANCE } from "@/lib/content";

const SAMPLE_STATS = [["WIN STREAK", `${PERFORMANCE.winStreak} days`, "text-emerald-400"], ["THIS WEEK", PERFORMANCE.thisWeek, "text-emerald-400"], ["THIS MONTH", PERFORMANCE.thisMonth, "text-emerald-400"], ["ALL TIME", PERFORMANCE.allTime, "text-emerald-400"]];
const SAMPLE_RECENT = RECENT_RESULTS.map((r) => ({ date: r.date, league: r.league, label: r.pick, status: r.result === "WIN" ? "W" : "L", units: parseFloat(r.units) }));

const RES = { W: ["WIN", "text-emerald-400"], L: ["LOSS", "text-red-400"], P: ["PUSH", "text-slate-400"] };

export default function LeaderboardPage() {
  const perf = usePerformance();
  const real = perf?.ready;
  const recent = real ? perf.recent : SAMPLE_RECENT;
  const stats = !real ? SAMPLE_STATS : [["WIN STREAK", `${perf?.win_streak ?? 0} days`, "text-emerald-400"], ["LAST 7 DAYS", perf?.last7?.record, unitColor(perf?.last7?.units)], ["THIS MONTH", fmtUnits(perf?.this_month?.units), unitColor(perf?.this_month?.units)], ["ALL TIME", fmtUnits(perf?.all_time?.units), unitColor(perf?.all_time?.units)]];
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 space-y-10">
      <div>
        <div className="text-xs font-bold tracking-[0.2em] text-smasher-orange mb-1">TRACK RECORD</div>
        <h1 className="font-heading font-black uppercase text-4xl sm:text-5xl text-gradient-gold flex items-center gap-3"><Trophy className="w-9 h-9 text-smasher-gold" /> Leaderboard</h1>
        {real && <p className="text-sm text-slate-400 mt-2" data-testid="leaderboard-real-note">The real, graded record of every free pick we post · tracking since {sinceLabel(perf?.tracking_since)}</p>}
      </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {stats.map(([k, v, c]) => (
            <div key={k} className="panel p-5 text-center" data-testid={`leaderboard-stat-${k.toLowerCase().replace(/ /g, "-")}`}>
              <div className="text-[10px] text-slate-500 tracking-wide">{k}</div>
              <div className={`font-heading font-black text-2xl mt-1 ${c}`}>{v || "—"}</div>
            </div>
          ))}
        </div>

      <div className="panel p-5" data-testid="leaderboard-recent">
        <div className="flex items-center gap-2 font-heading font-bold tracking-wide text-smasher-gold mb-4"><TrendingUp className="w-5 h-5" /> RECENT RESULTS</div>
        {real && !recent.length && <p className="text-sm text-slate-500">No graded picks yet.</p>}
        <div className="space-y-1">
          {recent.map((r, i) => (
            <div key={i} className="flex items-center gap-3 py-2.5 border-b border-white/5 last:border-0 text-sm" data-testid={`graded-pick-${i}`}>
              <span className="text-slate-500 font-mono-num w-24 shrink-0 whitespace-nowrap text-xs hidden sm:block">{r.date}</span>
              <span className="text-smasher-gold font-bold w-12 sm:w-14 shrink-0 text-xs sm:text-sm">{r.league}</span>
              <span className="text-slate-200 flex-1 min-w-0"><span className="block truncate">{r.label}</span><span className="block text-[11px] text-slate-500 truncate"><span className="sm:hidden">{r.date}{r.final_score ? " · " : ""}</span>{r.final_score}</span></span>
              <span className={`font-bold ${RES[r.status][1]}`}>{RES[r.status][0]}</span>
              <span className={`font-mono-num w-16 text-right ${RES[r.status][1]}`}>{fmtUnits(r.units)}</span>
            </div>
          ))}
        </div>
      </div>

      <PremiumPicksBanner />
      <p className="text-xs text-slate-600 text-center">{real ? "Free picks graded as posted, 1 unit each." : "Performance figures are illustrative."} Past results do not guarantee future outcomes.</p>
      <ResponsibleBanner />
    </div>
  );
}
